import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  GlassHeroContainer,
} from '../../components/glassmorphism/GlassmorphismStyles';
import GetStarted from '../../components/GetStarted/GetStarted';

const AboutUsPage: React.FC = () => {
  return (
    <GlassHeroContainer>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 14, sm: 20 },
          pb: { xs: 8, sm: 12 },
        }}
      >
        <AboutUsContainer>
          {/* Heading Section */}
          <HeadingSection>
            <HeadingWrapper>
              <Heading>About Us</Heading>
            </HeadingWrapper>
          </HeadingSection>

          {/* Content Section */}
          <ContentSection>
            <ContentWrapper>
              <BodyText>
                WOM is <strong>Word of Mouth in a modern format</strong> — a trusted recommendation network that's always within reach, on your smartphone or computer. We created a platform that helps people find verified service providers easily, without chaos, extra costs, or endless searching.
              </BodyText>

              <BodyText>
                <strong>Our goal is to make everyday life simpler.</strong> WOM brings people together so they can rely on each other and find help exactly when it's needed. The platform reduces expenses for both providers and customers, making collaboration transparent, fair, and comfortable.
              </BodyText>

              <BodyText>
                <strong>From people, for people.</strong>
                <br />
                We value reliability, simplicity, and a human touch. WOM welcomes professionals who work offline as well as those who offer services online — all becoming accessible to customers in a clear and convenient way.
                <br />
                We are building not just a service, but a space of trust and mutual support.
              </BodyText>

              <BodyText>
                The power of word of mouth — now in a <strong>convenient digital form.</strong>
              </BodyText>
            </ContentWrapper>
          </ContentSection>
        </AboutUsContainer>

        {/* Get Started CTA */}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 8, mb: 6 }}>
          <GetStarted />
        </Box>
      </Container>
    </GlassHeroContainer>
  );
};

// Main container - Frame 8
const AboutUsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '0px',
  gap: '24px',
  width: '100%',
  maxWidth: '1150px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    padding: '0 20px',
  },
}));

// Frame 4 - Heading Section
const HeadingSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '48px',
  width: '100%',
  maxWidth: '1150px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Frame 5 - Heading Wrapper
const HeadingWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// About Us Heading
const Heading = styled(Typography)(({ theme }) => ({
  width: '100%',
  maxWidth: '760px',
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '60px',
  lineHeight: '120%',
  textAlign: 'center',
  letterSpacing: '-0.5px',
  color: theme.palette.mode === 'dark' ? '#00FFFF' : '#005CAD',

  [theme.breakpoints.down('md')]: {
    fontSize: '48px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '36px',
  },
}));

// Frame 5 - Content Section
const ContentSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '8px',
  width: '100%',
  maxWidth: '1150px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Frame 6 - Content Wrapper
const ContentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Body Text
const BodyText = styled(Typography)(({ theme }) => ({
  width: '100%',
  fontFamily: 'Roboto',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '150%',
  letterSpacing: '0.15px',
  color: theme.palette.mode === 'dark' ? '#B6B6B6' : '#1A1A1A',
  textAlign: 'left',

  '& strong': {
    fontWeight: 700,
  },
}));

export default AboutUsPage;
